import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import {
  Brain,
  Plus,
  Edit,
  Trash2,
  Save,
  Loader2,
  AlertTriangle,
  CheckCircle,
  FileText,
  Lightbulb,
  Settings,
  RefreshCw,
  Info
} from 'lucide-react';
import api from '@/lib/api';

const RULE_TYPES = [
  { value: 'ignore', label: 'Ignore Condition', description: 'Skip certain checks for specific scenarios' },
  { value: 'adjust_scoring', label: 'Adjust Scoring', description: 'Modify how scores are calculated' },
  { value: 'context', label: 'Add Context', description: 'Provide additional context for analysis' },
  { value: 'example', label: 'Example Scenario', description: 'Train with specific examples' }
];

const PRESET_RULES = [
  {
    rule_type: 'ignore',
    title: 'Voicemail - Skip Disclaimer Check',
    description: 'Voicemails do not require a disclaimer statement',
    condition: 'call_type == "voicemail"',
    action: 'skip_disclaimer_check'
  },
  {
    rule_type: 'ignore',
    title: 'Short Calls - Reduce Requirements',
    description: 'Calls under 30 seconds should have minimal requirements',
    condition: 'duration < 30',
    action: 'minimal_scoring'
  },
  {
    rule_type: 'context',
    title: 'Indian Accent Awareness',
    description: 'The LLM should be more lenient with transcription accuracy for Indian accents',
    condition: 'always',
    action: 'improve_accent_handling'
  },
  {
    rule_type: 'adjust_scoring',
    title: 'First-Time Callers',
    description: 'Be more lenient with scoring for recruiters on their first few calls',
    condition: 'user_call_count < 5',
    action: 'lenient_scoring'
  }
];

export default function FineTuneLLM() {
  const [config, setConfig] = useState({
    rules: [],
    global_instructions: '',
    scoring_adjustments: {}
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddRule, setShowAddRule] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState({
    rule_type: 'ignore',
    title: '',
    description: '',
    condition: '',
    action: '',
    example_input: '',
    example_output: '',
    is_active: true
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get('/llm-fine-tune');
      setConfig(data);
    } catch (error) {
      console.error('Failed to load config:', error);
      toast.error('Failed to load LLM configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const saveGlobalInstructions = async () => {
    setIsSaving(true);
    try {
      await api.put('/llm-fine-tune/global-instructions', { instructions: config.global_instructions });
      toast.success('Global instructions saved');
    } catch (error) {
      toast.error('Failed to save instructions');
    } finally {
      setIsSaving(false);
    }
  };

  const addRule = async () => {
    if (!newRule.title || !newRule.description) {
      toast.error('Please fill in title and description');
      return;
    }
    
    setIsSaving(true);
    try {
      const { data } = await api.post('/llm-fine-tune/rule', newRule);
      setConfig(prev => ({
        ...prev,
        rules: [...prev.rules, data.rule]
      }));
      setShowAddRule(false);
      setNewRule({
        rule_type: 'ignore',
        title: '',
        description: '',
        condition: '',
        action: '',
        example_input: '',
        example_output: '',
        is_active: true
      });
      toast.success('Rule added successfully');
    } catch (error) {
      toast.error('Failed to add rule');
    } finally {
      setIsSaving(false);
    }
  };

  const updateRule = async (rule) => {
    setIsSaving(true);
    try {
      await api.put(`/llm-fine-tune/rule/${rule.id}`, rule);
      setConfig(prev => ({
        ...prev,
        rules: prev.rules.map(r => r.id === rule.id ? rule : r)
      }));
      setEditingRule(null);
      toast.success('Rule updated');
    } catch (error) {
      toast.error('Failed to update rule');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteRule = async (ruleId) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      await api.delete(`/llm-fine-tune/rule/${ruleId}`);
      setConfig(prev => ({
        ...prev,
        rules: prev.rules.filter(r => r.id !== ruleId)
      }));
      toast.success('Rule deleted');
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const toggleRuleActive = async (rule) => {
    const updatedRule = { ...rule, is_active: !rule.is_active };
    await updateRule(updatedRule);
  };

  const addPresetRule = (preset) => {
    setNewRule({
      ...preset,
      is_active: true,
      example_input: '',
      example_output: ''
    });
    setShowAddRule(true);
  };

  const getRuleTypeBadge = (type) => {
    const colors = {
      ignore: 'bg-amber-100 text-amber-800',
      adjust_scoring: 'bg-blue-100 text-blue-800',
      context: 'bg-purple-100 text-purple-800',
      example: 'bg-green-100 text-green-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fine-tune-llm">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-7 w-7 text-purple-600" />
            Fine-Tune LLM
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure rules and examples to improve AI analysis accuracy
          </p>
        </div>
        <Button variant="outline" onClick={loadConfig}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-blue-800 font-medium">How Fine-Tuning Works</p>
              <p className="text-blue-700 text-sm mt-1">
                Rules you create here will be reviewed by the AI when processing calls. 
                Use this to handle special cases like voicemails, adjust scoring criteria, 
                or provide examples of ideal responses.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Global Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Global Instructions
          </CardTitle>
          <CardDescription>
            These instructions are always included when the AI analyzes calls
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Enter global instructions for the AI...&#10;&#10;Example:&#10;- Be aware that calls may have Indian accents&#10;- Focus on key compliance points&#10;- Be lenient with background noise issues"
            value={config.global_instructions || ''}
            onChange={(e) => setConfig(prev => ({ ...prev, global_instructions: e.target.value }))}
            className="min-h-[150px]"
          />
          <Button onClick={saveGlobalInstructions} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Instructions
          </Button>
        </CardContent>
      </Card>

      {/* Preset Rules */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            Quick Add Presets
          </CardTitle>
          <CardDescription>
            Common scenarios you can quickly add to your rules
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {PRESET_RULES.map((preset, i) => (
              <div
                key={i}
                className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => addPresetRule(preset)}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{preset.title}</span>
                  <Badge className={getRuleTypeBadge(preset.rule_type)}>
                    {preset.rule_type}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{preset.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Custom Rules */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Custom Rules ({config.rules?.length || 0})
              </CardTitle>
              <CardDescription>
                Your custom rules for call processing
              </CardDescription>
            </div>
            <Dialog open={showAddRule} onOpenChange={setShowAddRule}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Rule
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Rule</DialogTitle>
                  <DialogDescription>
                    Create a rule to customize how the AI processes calls
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Rule Type</Label>
                      <Select
                        value={newRule.rule_type}
                        onValueChange={(v) => setNewRule(prev => ({ ...prev, rule_type: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RULE_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={newRule.title}
                        onChange={(e) => setNewRule(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="e.g., Skip disclaimer for voicemails"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Textarea
                      value={newRule.description}
                      onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Explain what this rule does and when it should apply..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Condition</Label>
                      <Input
                        value={newRule.condition}
                        onChange={(e) => setNewRule(prev => ({ ...prev, condition: e.target.value }))}
                        placeholder="e.g., call_type == 'voicemail'"
                      />
                      <p className="text-xs text-muted-foreground">When should this rule apply?</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Action</Label>
                      <Input
                        value={newRule.action}
                        onChange={(e) => setNewRule(prev => ({ ...prev, action: e.target.value }))}
                        placeholder="e.g., skip_disclaimer_check"
                      />
                      <p className="text-xs text-muted-foreground">What should the AI do?</p>
                    </div>
                  </div>

                  {newRule.rule_type === 'example' && (
                    <>
                      <div className="space-y-2">
                        <Label>Example Input</Label>
                        <Textarea
                          value={newRule.example_input}
                          onChange={(e) => setNewRule(prev => ({ ...prev, example_input: e.target.value }))}
                          placeholder="Paste an example transcript or scenario..."
                          className="min-h-[100px]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Expected Output</Label>
                        <Textarea
                          value={newRule.example_output}
                          onChange={(e) => setNewRule(prev => ({ ...prev, example_output: e.target.value }))}
                          placeholder="Describe the expected analysis or scoring..."
                          className="min-h-[100px]"
                        />
                      </div>
                    </>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddRule(false)}>Cancel</Button>
                  <Button onClick={addRule} disabled={isSaving}>
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                    Add Rule
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {config.rules?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Brain className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No custom rules yet</p>
              <p className="text-sm">Add rules to customize how the AI analyzes calls</p>
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-2">
              {config.rules?.map((rule) => (
                <AccordionItem
                  key={rule.id}
                  value={rule.id}
                  className={`border rounded-lg px-4 ${!rule.is_active ? 'opacity-50 bg-muted/50' : ''}`}
                >
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3 flex-1">
                      <Switch
                        checked={rule.is_active}
                        onCheckedChange={() => toggleRuleActive(rule)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Badge className={getRuleTypeBadge(rule.rule_type)}>
                        {rule.rule_type}
                      </Badge>
                      <span className="font-medium">{rule.title}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-4">
                    <div className="space-y-3">
                      <p className="text-muted-foreground">{rule.description}</p>
                      
                      {rule.condition && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">Condition:</span>
                          <code className="bg-muted px-2 py-1 rounded">{rule.condition}</code>
                        </div>
                      )}
                      
                      {rule.action && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">Action:</span>
                          <code className="bg-muted px-2 py-1 rounded">{rule.action}</code>
                        </div>
                      )}

                      {rule.example_input && (
                        <div className="space-y-1">
                          <span className="text-sm text-muted-foreground">Example Input:</span>
                          <pre className="bg-muted p-2 rounded text-xs overflow-x-auto">{rule.example_input}</pre>
                        </div>
                      )}

                      {rule.example_output && (
                        <div className="space-y-1">
                          <span className="text-sm text-muted-foreground">Expected Output:</span>
                          <pre className="bg-muted p-2 rounded text-xs overflow-x-auto">{rule.example_output}</pre>
                        </div>
                      )}

                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingRule(rule)}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteRule(rule.id)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Edit Rule Dialog */}
      {editingRule && (
        <Dialog open={!!editingRule} onOpenChange={() => setEditingRule(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Rule</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rule Type</Label>
                  <Select
                    value={editingRule.rule_type}
                    onValueChange={(v) => setEditingRule(prev => ({ ...prev, rule_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RULE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={editingRule.title}
                    onChange={(e) => setEditingRule(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingRule.description}
                  onChange={(e) => setEditingRule(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Condition</Label>
                  <Input
                    value={editingRule.condition || ''}
                    onChange={(e) => setEditingRule(prev => ({ ...prev, condition: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Action</Label>
                  <Input
                    value={editingRule.action || ''}
                    onChange={(e) => setEditingRule(prev => ({ ...prev, action: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingRule(null)}>Cancel</Button>
              <Button onClick={() => updateRule(editingRule)} disabled={isSaving}>
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
